// --------------------------
// IMPORTS
// --------------------------

import { useEffect, useState } from "react";
import { message, Modal } from "antd";
import CustomLoader from "../utils/CustomLoader";
import { useDispatch, useSelector } from "react-redux";
import SubmitButton from "../components/atoms/Buttons/SubmitButton";
import SelectOptn from "../components/atoms/SelectOption";
import InputField from "../components/atoms/InputField";
import { fileUploadThunk } from "../store/thunks/fileUploadThunk";
import { resetFileUpload } from "../store/slices/fileUploadSlice";

// --------------------------
// MAIN COMPONENT
// --------------------------
const FileUpload = ({ isVisible, setIsVisible, role }) => {
  const dispatch = useDispatch();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const { data, loading, error } = useSelector((state) => state.fileUpload);

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(false);

  const initialFormState = {
    model: null,
    file: null,
    resetKey: Date.now(),
  };
  const [formState, setFormState] = useState(initialFormState);

  // --------------------------
  // USE EFFECTS
  // --------------------------
  // Show success/error messages
  useEffect(() => {
    if (data) {
      message.success("Data Imported successfully.");
      resetForm();
      setIsVisible(false);
      dispatch(resetFileUpload());
    }
    if (error) {
      message.error(error);
      dispatch(resetFileUpload());
    }
  }, [data, error]);

  // --------------------------
  // HANDLERS
  // --------------------------

  // Close modal
  const closeModal = () => {
    dispatch(resetFileUpload());
    resetForm();
    setIsVisible(false);
  };

  // Handle file input change
  const handleFileChange = (e) => {
    setFormState({ ...formState, file: e.target.files[0] });
  };

  // Handle select change
  const handleSelectChange = (value) => {
    setFormState({ ...formState, model: value });
  };

  // Submit form
  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowLoader(true);

    const { file, model } = formState;
    if (!file) {
      message.error("Please choose a file before continuing.");
      setShowLoader(false);
      return;
    }

    const allowedExtensions = ["xlsx", "xls"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      message.error("Invalid file type! Please select a .xlsx or .xls file.");
      setShowLoader(false);
      return;
    }

    const formData = new FormData();
    formData.append("role", role);
    formData.append("model", model);
    formData.append("file", file);
    formData.append("fileName", file.name);

    try {
      //Dispatch thunk
      await dispatch(fileUploadThunk(formData));
    } catch (err) {
      message.error("There was an error! " + err);
    } finally {
      setShowLoader(false);
    }
  };

  // Reset entire form
  const resetForm = () => {
    setFormState({
      model: null,
      file: null,
      resetKey: Date.now(), // changing key forces SelectOptn to remount
    });
  };

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <Modal
      title="File Upload"
      open={isVisible}
      onCancel={closeModal}
      className="importDataModal"
      footer={[
        <SubmitButton
          ky="reset"
          type="primary"
          htmlType="button"
          submitName="Reset"
          className="btn ml-auto px-5 py-2 defaultWhiteBtn rounded-lg"
          onClick={resetForm}
        />,
        <SubmitButton
          ky="import"
          type="primary"
          htmlType="submit"
          submitName="Import"
          className="btn ml-auto px-5 py-2 defaultbutton rounded-lg"
          onClick={handleSubmit}
        />,
      ]}
      destroyOnClose
    >
      <div className="importBulk">
        <CustomLoader showLoader={showLoader} />
        <form>
          <SelectOptn
            key={formState.resetKey} // force remount on reset
            name="model"
            size="large"
            placeholder="Select an option"
            allowClear
            value={formState.model}
            onChange={handleSelectChange}
            options={[
              { label: "Expense Code", value: "expenseCode" },
              { label: "Departments", value: "departments" },
              { label: "Project Code", value: "projectCode" },
              { label: "Users", value: "users" },
              { label: "Vendors", value: "vendors" },
              {
                label: "Update User's Project Code",
                value: "updateUserProjectCode",
              },
            ]}
          />

          <InputField
            placeholder=""
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            value={formState.file ? undefined : ""}
          />
          {formState.file && (
            <span className="file-name">{formState.file.name}</span>
          )}
          {/* <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileChange}
            value={formState.file ? undefined : ""} // clears file input
          /> */}
        </form>
      </div>
    </Modal>
  );
};

export default FileUpload;
